import { useMemo, useState } from 'react';
import { useOutletContext, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ShoppingBag, Truck, Upload, Zap } from 'lucide-react';

import { CategoryNav } from '@/components/catalog/CategoryNav';
import { SearchBar } from '@/components/catalog/SearchBar';
import { CatalogGrid } from '@/components/catalog/CatalogGrid';
import { CartSidebar } from '@/components/cart/CartSidebar';
import { OrderHistory } from '@/components/orders/OrderHistory';
import { LocationPicker } from '@/components/layout/LocationPicker';
import { Button } from '@/components/ui/button';
import type { ShellOutletCtx } from '@/lib/shell-context';
import { cn } from '@/lib/utils';
import { useCatalog } from '@/hooks/useCatalog';
import { useCustomerPricing } from '@/hooks/useCustomerPricing';
import { useCart } from '@/hooks/useCart';
import { useActivity } from '@/hooks/useActivity';
import { useOrderHistory } from '@/hooks/useOrderHistory';
import { useReorder } from '@/hooks/useReorder';
import { reorderMessage } from '@/components/orders/ReorderButton';
import { CATEGORIES, type Category as Cat } from '@/lib/constants';
import { isPickupItem, displayName } from '@/types/domain';

type ShopTab = 'shop' | 'history';

/**
 * Order page — catalog grid + right-rail cart.
 *
 * Three flex children inside main: the catalog section (scrolls), the
 * CartSidebar (scrolls on its own on lg+). On mobile the CartSidebar hides
 * and BottomNav links to /cart for the full-screen review.
 *
 * Data source: useCatalog (Supabase → fixture fallback) + useCustomerPricing
 * (Supabase → Melt fixture). Cart state: useCart (localStorage today, server
 * draft in the next session).
 */
export default function Order() {
  const navigate = useNavigate();
  const { selectedLocation, selectedLocationId, setSelectedLocationId } =
    useOutletContext<ShellOutletCtx>();
  const { data: allItems = [], isLoading } = useCatalog();
  // Pickup/return rows never show as shop products — tank returns are asked
  // for during submit (Cart's tank-return flow).
  const items = useMemo(() => allItems.filter((it) => !isPickupItem(it)), [allItems]);
  const { data: pricing } = useCustomerPricing();
  const { cart, setQty, adjustQty, remove } = useCart();
  const { data: activity } = useActivity();
  // Order history powers "My Collection" — the customer's regulars, from
  // portal orders + QBO invoice history (same source as /collection).
  const { data: history, isLoading: historyLoading } = useOrderHistory();
  const { reorderById } = useReorder();

  // Catalog items this customer has ordered before.
  const collectionIds = useMemo(() => {
    const freq = history?.byCatalogItemId;
    const ids = new Set<string>();
    if (freq) {
      for (const it of items) if (freq.has(it.id)) ids.add(it.id);
    }
    return ids;
  }, [items, history]);
  const hasCollection = collectionIds.size > 0;

  // Most recent portal order, for the "Reorder last" header button.
  const lastOrderId = useMemo(() => {
    const rows = activity ?? [];
    return rows.find((r) => r.source === 'order' && Boolean(r.rowId))?.rowId ?? null;
  }, [activity]);

  const [reorderBusy, setReorderBusy] = useState(false);

  async function reorderLast() {
    if (!lastOrderId || reorderBusy) return;
    setReorderBusy(true);
    try {
      const summary = await reorderById(lastOrderId);
      if (summary && summary.added > 0) {
        navigate('/cart');
      } else {
        // Nothing landed in the cart — surface why without leaving the page.
        window.alert(reorderMessage(summary));
      }
    } finally {
      setReorderBusy(false);
    }
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const tab: ShopTab = searchParams.get('tab') === 'history' ? 'history' : 'shop';
  const setTab = (next: ShopTab) => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (next === 'history') sp.set('tab', 'history');
        else sp.delete('tab');
        return sp;
      },
      { replace: true },
    );
  };

  // null = the customer hasn't clicked a pill yet. Returning customers land
  // on My Collection (their regulars); customers with no history land on the
  // regular catalog default. Optimistically assume a collection exists while
  // history loads — most customers have QBO invoice history, so this avoids
  // a default→collection flip on every visit.
  const [pickedCat, setPickedCat] = useState<Cat | null>(null);
  const activeCat: Cat =
    pickedCat ?? (historyLoading || hasCollection ? 'My Collection' : '3-gal BIB');
  const [search, setSearch] = useState('');

  const counts = useMemo<Partial<Record<Cat, number>>>(() => {
    const out: Partial<Record<Cat, number>> = {
      'My Collection': collectionIds.size,
      All: items.length,
    };
    for (const c of CATEGORIES) {
      if (c === 'All' || c === 'My Collection') continue;
      out[c] = items.filter((it) => it.category === c).length;
    }
    return out;
  }, [items, collectionIds]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matches = items.filter((p) => {
      if (activeCat === 'My Collection') {
        if (!collectionIds.has(p.id)) return false;
      } else if (activeCat !== 'All' && p.category !== activeCat) {
        return false;
      }
      if (term) {
        const nameHit =
          p.name.toLowerCase().includes(term) ||
          displayName(p).toLowerCase().includes(term);
        const skuHit = p.sku ? p.sku.toLowerCase().includes(term) : false;
        if (!nameHit && !skuHit) return false;
      }
      return true;
    });
    // Regulars float to the top: My Collection sorts by how often the item
    // is ordered; on All the collection leads and the rest keep catalog
    // order (Array.sort is stable).
    if (activeCat === 'My Collection' || activeCat === 'All') {
      const freq = history?.byCatalogItemId;
      matches.sort(
        (a, b) => (freq?.get(b.id)?.orderCount ?? 0) - (freq?.get(a.id)?.orderCount ?? 0),
      );
    }
    return matches;
  }, [items, activeCat, search, collectionIds, history]);

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar — Shop (catalog) | Order history (submitted orders) */}
      <div className="shrink-0 flex justify-center gap-2 px-6 md:px-8 pt-5 pb-3 border-b border-brix-border bg-brix-surface/85 backdrop-blur-xl">
        <ShopTabButton active={tab === 'shop'} onClick={() => setTab('shop')} icon={ShoppingBag} label="Shop" />
        <ShopTabButton active={tab === 'history'} onClick={() => setTab('history')} icon={Truck} label="Order history" />
      </div>

      {tab === 'history' ? (
        <div className="flex-1 overflow-y-auto scrollbar-thin px-6 md:px-8 pt-8 pb-20 md:pb-12">
          <OrderHistory />
        </div>
      ) : (
        <div className="flex flex-1 min-h-0">
      <section className="flex-1 min-w-0 flex flex-col">
        {/* Sticky page header — title, actions, search, category pills. */}
        <div
          className="sticky top-0 z-10 px-6 md:px-8 pt-6 pb-4 border-b border-brix-border bg-brix-surface/85 backdrop-blur-xl"
        >
          <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
            <div>
              <h1 className="font-display text-3xl font-semibold tracking-tight text-brix-ink">
                {activeCat === 'All' ? 'Full Catalog' : activeCat}
              </h1>
              <p className="text-sm mt-0.5 text-brix-muted">
                {activeCat === 'My Collection'
                  ? 'Items you order most, first'
                  : 'Your pricing'}{' '}
                · 24–48 hour delivery · {filtered.length} items
              </p>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              {/* Only master accounts (>1 location) see this; 1:1 accounts
                  order against their single primary location silently. */}
              <LocationPicker
                selectedLocationId={selectedLocationId}
                setSelectedLocationId={setSelectedLocationId}
              />
              <Button type="button" size="sm" variant="secondary" disabled>
                <Upload size={13} /> Quick order (paste SKUs)
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={!lastOrderId || reorderBusy}
                onClick={reorderLast}
              >
                {reorderBusy ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Zap size={13} />
                )}{' '}
                Reorder last
              </Button>
            </div>
          </div>

          <SearchBar value={search} onChange={setSearch} />

          <div className="mt-3">
            <CategoryNav active={activeCat} onSelect={setPickedCat} counts={counts} />
          </div>
        </div>

        {/* Scrollable grid. While history is still resolving and no pill has
            been clicked, keep the spinner up so My Collection doesn't flash
            empty before the regulars land. */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-6 md:px-8 py-6 pb-20 md:pb-10">
          {isLoading || (pickedCat === null && historyLoading) ? (
            <div className="text-center py-16 flex flex-col items-center gap-2">
              <Loader2 size={20} className="animate-spin text-brix-primary" />
              <p className="text-sm text-brix-muted">Loading catalog…</p>
            </div>
          ) : (
            <CatalogGrid
              items={filtered}
              pricing={pricing}
              cart={cart}
              onAdjust={adjustQty}
              onSet={setQty}
            />
          )}
        </div>
      </section>

      <CartSidebar
        items={items}
        pricing={pricing}
        cart={cart}
        locationName={selectedLocation}
        onAdjust={adjustQty}
        onSet={setQty}
        onRemove={remove}
        onSubmit={() => navigate('/cart')}
        onSaveDraft={() => {
          // Next session: persist to orders.order_drafts.
          // For now this is a no-op; localStorage is already keeping the cart.
        }}
      />
        </div>
      )}
    </div>
  );
}

function ShopTabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof ShoppingBag;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'inline-flex items-center gap-2 px-6 py-2.5 text-sm rounded-full transition-all duration-200 cursor-pointer',
        active
          ? 'bg-brix-primary text-white font-semibold shadow-card'
          : 'text-brix-muted hover:bg-brix-primary-tint/60 hover:text-brix-ink',
      )}
    >
      <Icon size={15} /> {label}
    </button>
  );
}
